import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRightLeft, Download, Upload, X, ExternalLink } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { importBackup } from "../lib/secureStore";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const PRIMARY = process.env.REACT_APP_PRIMARY_HOST;
const LEGACY = process.env.REACT_APP_LEGACY_HOST;
const WELCOME_DONE = "hb-move-welcome-done";
const BANNER_DONE = "hb-move-banner-done";
const host = () => window.location.hostname.replace(/^www\./, "");
export const onPrimary = () => !!PRIMARY && host() === PRIMARY;
export const onLegacy = () => !!LEGACY && !!PRIMARY && host() === LEGACY;

// Shown on a fresh start (no data): how to bring a backup over from the previous address
export function MoveWelcome() {
  const { restoreBackup } = useStore();
  const [hidden, setHidden] = useState(() => localStorage.getItem(WELCOME_DONE) === "1");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (hidden) return null;
  const done = () => { localStorage.setItem(WELCOME_DONE, "1"); setHidden(true); };
  const restore = async (file) => {
    setBusy(true);
    try {
      const n = await restoreBackup(await importBackup(await file.text(), pw));
      localStorage.setItem(WELCOME_DONE, "1");
      toast.success(`Welcome back! Your budget is restored${n ? ` with ${n} receipt(s)` : ""}.`);
    } catch (e) {
      toast.error(e.message || "Couldn't read that backup file");
    }
    setBusy(false);
  };
  const steps = [
    <>Open your old address{LEGACY ? <> (<b>{LEGACY}</b>)</> : ""} on the device that has your data.</>,
    <>Go to <b>Settings → Backup</b>, keep <b>Include receipt photos</b> ticked, add a backup password, and tap <b>Backup</b>.</>,
    <>Come back here, enter that password below and choose the backup file.</>,
  ];
  return (
    <div className="rounded-2xl border-2 border-primary/40 bg-accent/50 p-5 mb-6 fade-up" data-testid="move-welcome">
      <div className="flex items-start gap-3">
        <ArrowRightLeft className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold" data-testid="move-welcome-title">{onPrimary() ? `Welcome to ${PRIMARY}! Moving from your old address?` : "Moving from another device or address?"}</p>
          <p className="text-sm text-muted-foreground mt-1">{onPrimary() ? "Your budget is stored privately in each web address, so it doesn't follow you here automatically. Bring it over in 3 steps:" : "Your budget lives only in the browser where you created it. Bring it over with an encrypted backup:"}</p>
          <ol className="mt-3 space-y-1.5 text-sm">{steps.map((s, i) => <li key={i} className="flex gap-2"><span className="num text-primary font-semibold">{i + 1}.</span><span>{s}</span></li>)}</ol>
          <div className="flex flex-wrap gap-2 mt-4">
            <Input type="password" placeholder="Backup password (if you set one)" value={pw} onChange={(e) => setPw(e.target.value)} className="max-w-xs bg-background" data-testid="move-restore-password-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="move-restore-button"><Upload className="h-4 w-4 mr-1" /> {busy ? "Restoring…" : "Restore backup"}</Button>
            <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => { e.target.files?.[0] && restore(e.target.files[0]); e.target.value = ""; }} data-testid="move-restore-file-input" />
            <Button variant="ghost" onClick={done} data-testid="move-welcome-dismiss">I'm starting fresh</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// One-time banner on the old address pointing to the new one
export function MoveBanner() {
  const [hidden, setHidden] = useState(() => localStorage.getItem(BANNER_DONE) === "1");
  if (hidden || !onLegacy()) return null;
  const close = () => { localStorage.setItem(BANNER_DONE, "1"); setHidden(true); };
  return (
    <div className="rounded-2xl border bg-accent/60 border-primary/30 px-4 py-3 mb-5 flex flex-wrap items-center gap-3 text-sm" data-testid="move-banner">
      <ArrowRightLeft className="h-4 w-4 text-primary shrink-0" />
      <span className="flex-1 min-w-[220px]"><b>Your app now lives at {PRIMARY}.</b> Make a backup here, then restore it there — your data doesn't move on its own.</span>
      <Button size="sm" asChild><Link to="/settings?backup=1" data-testid="move-banner-backup">
        <Download className="h-4 w-4 mr-1" /> Make backup</Link></Button>
      <Button size="sm" variant="outline" asChild><a href={`https://${PRIMARY}`} target="_blank" rel="noreferrer" data-testid="move-banner-open"><ExternalLink className="h-4 w-4 mr-1" /> Open {PRIMARY}</a></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={close} title="Don't show again" data-testid="move-banner-dismiss"><X className="h-4 w-4" /></Button>
    </div>
  );
}
